/**
 * AdminInvitations — admin page for managing registration invitations.
 *
 * Two sections:
 * 1. "Send Invitation" collapsible form card.
 * 2. "Invitations" table with filter chips (All / Active / Used / Expired)
 *    and per-row actions (Copy link, Resend, Revoke).
 *
 * Route: /admin/invitations (protected by AdminGuard in App.tsx)
 */
import { useState, useEffect, useCallback } from 'react';
import {
  ClipboardDocumentIcon,
  ArrowPathIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import Button from '@/components/Button';
import Input from '@/components/ui/Input';
import {
  listInvitations,
  createInvitation,
  revokeInvitation,
  resendInvitation,
} from '@/services/adminInvitationsApi';
import type {
  RegistrationInvitation,
  CreateInvitationPayload,
  InvitationStatus,
} from '@/types/admin';
import { formatDate } from '@/utils/formatters';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function deriveStatus(inv: RegistrationInvitation): InvitationStatus {
  if (inv.used_at) return 'used';
  if (new Date(inv.expires_at) <= new Date()) return 'expired';
  return 'active';
}

function buildRegistrationUrl(token: string): string {
  return `${window.location.origin}/register?token=${encodeURIComponent(token)}`;
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: InvitationStatus }) {
  const config: Record<InvitationStatus, { label: string; color: string; bg: string }> = {
    active: {
      label: 'Active',
      color: 'var(--color-accent)',
      bg: 'rgba(59,130,246,0.12)',
    },
    used: {
      label: 'Used',
      color: 'var(--color-accent)',
      bg: 'rgba(52,211,153,0.12)',
    },
    expired: {
      label: 'Expired',
      color: 'var(--color-accent-gold)',
      bg: 'rgba(251,191,36,0.12)',
    },
  };
  const c = config[status];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 500,
        color: status === 'used' ? 'var(--color-accent-green, #34D399)' : c.color,
        backgroundColor: status === 'used' ? 'rgba(52,211,153,0.12)' : c.bg,
      }}
    >
      {c.label}
    </span>
  );
}

// ─── Toast ────────────────────────────────────────────────────────────────────

interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onDismiss: () => void;
}

function Toast({ message, type, onDismiss }: ToastProps) {
  const isError = type === 'error';
  return (
    <div
      role="alert"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        padding: '14px 16px',
        borderRadius: '10px',
        backgroundColor: isError ? 'rgba(248,113,113,0.12)' : 'rgba(52,211,153,0.12)',
        border: `1px solid ${isError ? 'rgba(248,113,113,0.35)' : 'rgba(52,211,153,0.35)'}`,
        boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        maxWidth: '420px',
        color: 'var(--color-text-primary)',
        fontSize: '14px',
      }}
    >
      {isError ? (
        <ExclamationCircleIcon style={{ width: '18px', height: '18px', flexShrink: 0, color: '#F87171', marginTop: '1px' }} />
      ) : (
        <CheckCircleIcon style={{ width: '18px', height: '18px', flexShrink: 0, color: '#34D399', marginTop: '1px' }} />
      )}
      <span style={{ flex: 1, lineHeight: '1.5' }}>{message}</span>
      <button
        type="button"
        aria-label="Dismiss notification"
        onClick={onDismiss}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0', marginTop: '1px' }}
      >
        <XMarkIcon style={{ width: '16px', height: '16px' }} />
      </button>
    </div>
  );
}

// ─── Send Invitation Form ─────────────────────────────────────────────────────

interface SendFormProps {
  onCreated: (inv: RegistrationInvitation) => void;
}

function SendInvitationForm({ onCreated }: SendFormProps) {
  const [open, setOpen] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'coach' | 'athlete'>('coach');
  const [subTier, setSubTier] = useState<1 | 2>(1);
  const [coachTier, setCoachTier] = useState<'solo' | 'team' | 'enterprise'>('solo');
  const [evalLimit, setEvalLimit] = useState('');
  const [invLimit, setInvLimit] = useState('');
  const [expiresInDays, setExpiresInDays] = useState('7');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const payload: CreateInvitationPayload = {
      email: email.trim(),
      intended_role: role,
      intended_subscription_tier: subTier,
      expires_in_days: parseInt(expiresInDays, 10) || 7,
    };
    if (role === 'coach') {
      payload.intended_coach_tier = coachTier;
    }
    if (evalLimit !== '') {
      const v = parseInt(evalLimit, 10);
      if (!isNaN(v) && v >= 1) payload.monthly_eval_limit = v;
    }
    if (role === 'coach' && invLimit !== '') {
      const v = parseInt(invLimit, 10);
      if (!isNaN(v) && v >= 1) payload.monthly_invitations_limit = v;
    }

    try {
      const inv = await createInvitation(payload);
      onCreated(inv);
      // Reset form
      setEmail('');
      setRole('coach');
      setSubTier(1);
      setCoachTier('solo');
      setEvalLimit('');
      setInvLimit('');
      setExpiresInDays('7');
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'Failed to create invitation. Please try again.';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSubmitting(false);
    }
  };

  const selectStyle: React.CSSProperties = {
    width: '100%',
    height: '40px',
    borderRadius: '10px',
    border: '1px solid var(--color-border-subtle)',
    backgroundColor: 'var(--color-dark-elevated-2)',
    color: 'var(--color-text-primary)',
    padding: '0 12px',
    fontSize: '14px',
    outline: 'none',
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--color-dark-elevated-2)',
        border: '1px solid var(--color-border-subtle)',
        borderRadius: '14px',
        overflow: 'hidden',
        marginBottom: '24px',
      }}
    >
      {/* Card header — toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          color: 'var(--color-text-primary)',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: '16px',
          }}
        >
          Send Invitation
        </span>
        {open ? (
          <ChevronUpIcon style={{ width: '18px', height: '18px', color: 'var(--color-text-muted)' }} />
        ) : (
          <ChevronDownIcon style={{ width: '18px', height: '18px', color: 'var(--color-text-muted)' }} />
        )}
      </button>

      {/* Card body */}
      {open && (
        <div style={{ padding: '0 20px 20px' }}>
          <div style={{ height: '1px', backgroundColor: 'var(--color-border-subtle)', marginBottom: '20px' }} />

          {error && (
            <div
              role="alert"
              style={{
                padding: '10px 14px',
                borderRadius: '8px',
                backgroundColor: 'rgba(248,113,113,0.1)',
                border: '1px solid rgba(248,113,113,0.25)',
                color: '#F87171',
                fontSize: '13px',
                marginBottom: '16px',
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              {/* Email */}
              <div style={{ gridColumn: '1 / -1' }}>
                <Input
                  label="Email address *"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="coach@example.com"
                  autoComplete="off"
                />
              </div>

              {/* Intended role */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                  Intended role *
                </label>
                <select value={role} onChange={(e) => setRole(e.target.value as 'coach' | 'athlete')} style={selectStyle}>
                  <option value="coach">Coach</option>
                  <option value="athlete">Athlete</option>
                </select>
              </div>

              {/* Subscription tier */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                  Subscription tier
                </label>
                <select value={subTier} onChange={(e) => setSubTier(parseInt(e.target.value) as 1 | 2)} style={selectStyle}>
                  <option value={1}>Tier 1 — Standard</option>
                  <option value={2}>Tier 2 — Premium</option>
                </select>
              </div>

              {/* Coach tier (only when role=coach) */}
              {role === 'coach' && (
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--color-text-secondary)', marginBottom: '4px' }}>
                    Coach tier
                  </label>
                  <select
                    value={coachTier}
                    onChange={(e) => setCoachTier(e.target.value as 'solo' | 'team' | 'enterprise')}
                    style={selectStyle}
                  >
                    <option value="solo">Solo</option>
                    <option value="team">Team</option>
                    <option value="enterprise">Enterprise</option>
                  </select>
                </div>
              )}

              {/* Monthly eval limit */}
              <div>
                <Input
                  label="Monthly evaluation limit"
                  type="number"
                  min={1}
                  value={evalLimit}
                  onChange={(e) => setEvalLimit(e.target.value)}
                  placeholder="Unlimited"
                />
              </div>

              {/* Monthly invitations limit (coach only) */}
              {role === 'coach' && (
                <div>
                  <Input
                    label="Monthly invitations limit"
                    type="number"
                    min={1}
                    value={invLimit}
                    onChange={(e) => setInvLimit(e.target.value)}
                    placeholder="Unlimited"
                  />
                </div>
              )}

              {/* Expires in days */}
              <div>
                <Input
                  label="Expires in (days)"
                  type="number"
                  min={1}
                  max={90}
                  required
                  value={expiresInDays}
                  onChange={(e) => setExpiresInDays(e.target.value)}
                />
              </div>
            </div>

            <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" variant="primary" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send invitation'}
              </Button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// ─── Filter chips ─────────────────────────────────────────────────────────────

type FilterValue = 'all' | InvitationStatus;

function FilterChips({
  value,
  onChange,
}: {
  value: FilterValue;
  onChange: (v: FilterValue) => void;
}) {
  const chips: { value: FilterValue; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'used', label: 'Used' },
    { value: 'expired', label: 'Expired' },
  ];

  return (
    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {chips.map((chip) => {
        const active = chip.value === value;
        return (
          <button
            key={chip.value}
            type="button"
            onClick={() => onChange(chip.value)}
            style={{
              padding: '5px 14px',
              borderRadius: '20px',
              border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-border-subtle)'}`,
              backgroundColor: active ? 'rgba(59,130,246,0.12)' : 'transparent',
              color: active ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'all 120ms ease',
            }}
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

export default function AdminInvitations() {
  const [invitations, setInvitations] = useState<RegistrationInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterValue>('all');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const fetchInvitations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listInvitations(filter !== 'all' ? { status: filter as InvitationStatus } : {});
      setInvitations(data.items);
    } catch {
      showToast('Failed to load invitations.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter, showToast]);

  useEffect(() => {
    fetchInvitations();
  }, [fetchInvitations]);

  const handleCreated = useCallback(
    (inv: RegistrationInvitation) => {
      showToast(`Invitation sent to ${inv.email}.`);
      // Copy the link automatically
      const url = buildRegistrationUrl(inv.token);
      navigator.clipboard.writeText(url).catch(() => null);
      fetchInvitations();
    },
    [fetchInvitations, showToast]
  );

  const handleCopyLink = useCallback(
    (inv: RegistrationInvitation) => {
      const url = buildRegistrationUrl(inv.token);
      navigator.clipboard
        .writeText(url)
        .then(() => showToast('Registration link copied to clipboard.'))
        .catch(() => showToast('Could not copy — check browser permissions.', 'error'));
    },
    [showToast]
  );

  const handleResend = useCallback(
    async (inv: RegistrationInvitation) => {
      setActionInProgress(inv.id);
      try {
        await resendInvitation(inv.id);
        showToast(`Invitation resent to ${inv.email}.`);
        fetchInvitations();
      } catch {
        showToast('Failed to resend invitation.', 'error');
      } finally {
        setActionInProgress(null);
      }
    },
    [fetchInvitations, showToast]
  );

  const handleRevoke = useCallback(
    async (inv: RegistrationInvitation) => {
      if (!window.confirm(`Revoke invitation for ${inv.email}? This cannot be undone.`)) return;
      setActionInProgress(inv.id);
      try {
        await revokeInvitation(inv.id);
        showToast(`Invitation for ${inv.email} revoked.`);
        fetchInvitations();
      } catch {
        showToast('Failed to revoke invitation.', 'error');
      } finally {
        setActionInProgress(null);
      }
    },
    [fetchInvitations, showToast]
  );

  const cellStyle: React.CSSProperties = {
    padding: '12px 16px',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    borderBottom: '1px solid var(--color-border-subtle)',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
  };

  const headerCellStyle: React.CSSProperties = {
    padding: '10px 16px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    color: 'var(--color-text-muted)',
    borderBottom: '1px solid var(--color-border-subtle)',
    whiteSpace: 'nowrap',
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
      {/* Page header */}
      <div style={{ marginBottom: '28px' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 600,
            fontSize: '28px',
            color: 'var(--color-text-primary)',
            margin: 0,
          }}
        >
          Invitations
        </h1>
        <p style={{ marginTop: '6px', fontSize: '14px', color: 'var(--color-text-secondary)' }}>
          Send and manage platform registration invitations
        </p>
      </div>

      {/* Send invitation form */}
      <SendInvitationForm onCreated={handleCreated} />

      {/* Invitations table */}
      <div
        style={{
          backgroundColor: 'var(--color-dark-elevated-2)',
          border: '1px solid var(--color-border-subtle)',
          borderRadius: '14px',
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: '16px',
              color: 'var(--color-text-primary)',
            }}
          >
            Invitations
          </span>
          <FilterChips value={filter} onChange={setFilter} />
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--color-dark-elevated-1)' }}>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Email</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Role</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Tier</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Status</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Expires</th>
                <th style={{ ...headerCellStyle, textAlign: 'left' }}>Created</th>
                <th style={{ ...headerCellStyle, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{ ...cellStyle, textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}
                  >
                    Loading…
                  </td>
                </tr>
              ) : invitations.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    style={{ ...cellStyle, textAlign: 'center', padding: '40px', color: 'var(--color-text-muted)' }}
                  >
                    No invitations found
                  </td>
                </tr>
              ) : (
                invitations.map((inv) => {
                  const status = deriveStatus(inv);
                  const busy = actionInProgress === inv.id;

                  return (
                    <tr key={inv.id} style={{ transition: 'background-color 80ms ease' }}>
                      {/* Email */}
                      <td style={{ ...cellStyle, color: 'var(--color-text-primary)', fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {inv.email}
                      </td>

                      {/* Role */}
                      <td style={cellStyle}>
                        <span style={{ textTransform: 'capitalize' }}>{inv.intended_role}</span>
                      </td>

                      {/* Tier */}
                      <td style={cellStyle}>Tier {inv.intended_subscription_tier}</td>

                      {/* Status */}
                      <td style={cellStyle}>
                        <StatusBadge status={status} />
                      </td>

                      {/* Expires */}
                      <td style={cellStyle}>{formatDate(inv.expires_at)}</td>

                      {/* Created */}
                      <td style={cellStyle}>{formatDate(inv.created_at)}</td>

                      {/* Actions */}
                      <td style={{ ...cellStyle, textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                          {status === 'active' && (
                            <>
                              <button
                                type="button"
                                title="Copy registration link"
                                disabled={busy}
                                onClick={() => handleCopyLink(inv)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid var(--color-border-subtle)',
                                  backgroundColor: 'transparent',
                                  color: 'var(--color-text-secondary)',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <ClipboardDocumentIcon style={{ width: '13px', height: '13px' }} />
                                Copy link
                              </button>
                              <button
                                type="button"
                                title="Resend invitation email"
                                disabled={busy}
                                onClick={() => handleResend(inv)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid var(--color-border-subtle)',
                                  backgroundColor: 'transparent',
                                  color: 'var(--color-text-secondary)',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <ArrowPathIcon style={{ width: '13px', height: '13px' }} />
                                Resend
                              </button>
                              <button
                                type="button"
                                title="Revoke invitation"
                                disabled={busy}
                                onClick={() => handleRevoke(inv)}
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  border: '1px solid rgba(248,113,113,0.3)',
                                  backgroundColor: 'transparent',
                                  color: '#F87171',
                                  fontSize: '12px',
                                  cursor: 'pointer',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                <XMarkIcon style={{ width: '13px', height: '13px' }} />
                                Revoke
                              </button>
                            </>
                          )}
                          {status === 'used' && (
                            <span style={{ fontSize: '12px', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
                              Used
                            </span>
                          )}
                          {status === 'expired' && (
                            <button
                              type="button"
                              title="Resend with a new token"
                              disabled={busy}
                              onClick={() => handleResend(inv)}
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                border: '1px solid var(--color-border-subtle)',
                                backgroundColor: 'transparent',
                                color: 'var(--color-text-secondary)',
                                fontSize: '12px',
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <ArrowPathIcon style={{ width: '13px', height: '13px' }} />
                              Resend
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Toast notification */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}
